import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, Cpu, Hand, ShieldAlert, CheckCircle2, XCircle,
  AlertTriangle, ChevronDown, ChevronRight, ArrowRight,
} from "lucide-react";

const NIVEAUX = [["stop", "Bloquant"], ["warn", "Avertissement"], ["info", "Information"]];

// Convertit une ligne d'analyse en saisie persistable (surcharge moteur ou risque manuel complet).
function toSaisie(r) {
  if (r.origine === "manuel") {
    return {
      cle: r.cle, origine: "manuel", zonesLabel: r.zonesLabel || "", creneau: r.creneau || "",
      entreprise: r.entreprise || "", activite: r.activite || "", interference: r.interference || "",
      risque: r.risque || "", mesure: r.mesure || "", responsable: r.responsable || "",
      phase: r.phase || "", conditionBloquante: !!r.conditionBloquante, niveau: r.niveau || "warn",
      statut: r.statut || "retenu", justificationEcart: r.justificationEcart || "",
    };
  }
  return {
    cle: r.cle, mesure: r.mesure || "", responsable: r.responsable || "", phase: r.phase || "",
    conditionBloquante: !!r.conditionBloquante, statut: r.statut || "retenu",
    justificationEcart: r.justificationEcart || "",
  };
}

// Catégorie DÉCISIONNELLE figée sur l'état enregistré (serveur) pour éviter que les lignes
// changent de groupe pendant la saisie. "traiter" = bloquante sans mesure, "ecarte", sinon "maitrise".
function categorie(r) {
  if (!r) return "traiter"; // ligne nouvellement ajoutée (non encore enregistrée)
  if (r.statut === "ecarte") return "ecarte";
  if (r.conditionBloquante && !(r.mesure || "").trim()) return "traiter";
  return "maitrise";
}

export default function AnalyseRisques({ operationId, op, canEdit, onChange }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [baseline, setBaseline] = useState({}); // cle -> risque serveur (catégorie figée)
  const [open, setOpen] = useState({});          // cle -> détail déplié
  const [showMaitrise, setShowMaitrise] = useState(false);
  const [showEcarte, setShowEcarte] = useState(false);
  const [version, setVersion] = useState(op?.version);
  const [busy, setBusy] = useState(false);
  const phases = op?.phases || [];

  const applyServer = (res) => {
    const rs = res.risques.map((x) => ({ ...x }));
    setData(res);
    setRows(rs);
    const bl = {};
    rs.forEach((r) => { bl[r.cle] = { ...r }; });
    setBaseline(bl);
    // Ouverture par défaut : uniquement les lignes bloquantes ("à traiter").
    const op0 = {};
    rs.forEach((r) => { if (categorie(r) === "traiter") op0[r.cle] = true; });
    setOpen(op0);
  };

  const load = useCallback(() => {
    api.get(`/operations/${operationId}/analyse-risques`).then((r) => applyServer(r.data));
  }, [operationId]);
  useEffect(load, [load]);
  useEffect(() => { setVersion(op?.version); }, [op?.version]);

  const upRow = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const toggle = (cle) => setOpen((o) => ({ ...o, [cle]: !o[cle] }));
  const addManuel = () => {
    const cle = `USR-${Date.now().toString(36)}`;
    setRows((rs) => [...rs, {
      cle, origine: "manuel", ref: "", code: "",
      zonesLabel: "", creneau: "", entreprise: "", activite: "", interference: "", risque: "",
      mesure: "", responsable: "", phase: "", conditionBloquante: true, niveau: "stop", statut: "retenu",
      justificationEcart: "", niveauLabel: "Bloquant",
    }]);
    setOpen((o) => ({ ...o, [cle]: true }));
  };
  const rmRow = (i) => setRows((rs) => rs.filter((_, j) => j !== i));

  // Traiter maintenant : déplie la ligne, garantit son groupe visible, scrolle vers elle.
  const treat = (cle) => {
    const cat = categorie(baseline[cle]);
    if (cat === "maitrise") setShowMaitrise(true);
    if (cat === "ecarte") setShowEcarte(true);
    setOpen((o) => ({ ...o, [cle]: true }));
    setTimeout(() => {
      const el = document.getElementById(`ar-row-${cle}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { version, analyseRisques: rows.map(toSaisie) };
      const { data: res } = await api.put(`/operations/${operationId}/analyse-risques`, payload);
      applyServer(res); setVersion(res.version);
      toast.success("Analyse des risques enregistrée. Régénérez le plan pour la reporter dans le PDF.");
      onChange && onChange();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="mono">Analyse des risques…</div>;
  const s = data.synthese;

  // Groupes décisionnels (catégorie figée sur l'état enregistré).
  const groups = { traiter: [], maitrise: [], ecarte: [] };
  rows.forEach((r, i) => groups[categorie(baseline[r.cle])].push({ r, i }));

  const verdict = data.bloque
    ? `${data.pointsBloquants.length} interférence(s) à traiter avant démarrage`
    : s.total === 0
      ? "Aucune interférence détectée pour cette opération"
      : `Les ${s.retenus} interférence(s) retenue(s) sont maîtrisées`;
  const verdictColor = data.bloque ? "var(--rouge)" : "var(--vert)";

  return (
    <div className="card" data-testid="analyse-risques" style={{ borderLeft: `4px solid ${verdictColor}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <ShieldAlert size={18} color="var(--sarcelle)" />
        <h3 style={{ fontSize: "1rem", margin: 0 }}>Analyse des risques d'interférence</h3>
        <span className="tag" title="Source unique : moteur de coactivité + saisies">Cœur métier · §4</span>
      </div>

      {/* ===================== NIVEAU 1 · SYNTHÈSE DÉCISIONNELLE ===================== */}
      <div data-testid="ar-synthese-decision" style={{
        marginTop: 12, marginBottom: 14, padding: "14px 16px", borderRadius: 10,
        background: data.bloque ? "var(--rouge-p)" : "var(--vert-p)",
        border: `1px solid ${verdictColor}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {data.bloque ? <XCircle size={18} color={verdictColor} /> : <CheckCircle2 size={18} color={verdictColor} />}
          <b data-testid="ar-verdict" style={{ fontSize: ".95rem", color: verdictColor }}>{verdict}</b>
        </div>

        <div className="qg-summary" data-testid="ar-synthese" style={{ marginTop: 10 }}>
          <div className="qg-stat"><span className="v" data-testid="ar-count-maitrise">{s.avecMesure}/{s.retenus}</span><span className="l">Maîtrisées</span></div>
          <div className="qg-stat"><span className="v" data-testid="ar-count-traiter" style={{ color: data.bloque ? "var(--rouge)" : undefined }}>{s.bloquants}</span><span className="l">À traiter</span></div>
          <div className="qg-stat"><span className="v" data-testid="ar-count-ecarte">{s.ecartes}</span><span className="l">Écartées</span></div>
          <div className="qg-stat"><span className="v">{s.niveauMaxLabel}</span><span className="l">Niveau max</span></div>
        </div>

        {/* Points bloquants remontés en tête, orientés action */}
        {data.bloque && (
          <div data-testid="ar-points-bloquants" style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: ".78rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>
              À traiter en priorité
            </div>
            {data.pointsBloquants.map((p) => (
              <div key={p.cle} data-testid={`ar-bloc-item-${p.cle}`} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8,
                background: "var(--surface)", border: "1px solid var(--border)",
              }}>
                <XCircle size={15} color="var(--rouge)" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: ".85rem", fontWeight: 600 }}>{p.libelle}</div>
                  {p.detail && <div style={{ fontSize: ".76rem", color: "var(--text-muted)" }}>{p.detail}</div>}
                </div>
                <button className="btn btn-sm btn-p" data-testid={`ar-treat-${p.cle}`} onClick={() => treat(p.cle)}
                  style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  Traiter <ArrowRight size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===================== NIVEAU 2 · DÉTAIL (profondeur à la demande) ===================== */}
      {/* Groupe : à traiter (ouvert par défaut) */}
      {groups.traiter.length > 0 && (
        <div data-testid="ar-group-traiter" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: ".8rem", fontWeight: 700, color: "var(--rouge)", margin: "4px 0 8px", display: "flex", alignItems: "center", gap: 6 }}>
            <AlertTriangle size={15} /> À traiter ({groups.traiter.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {groups.traiter.map(({ r, i }) => renderRow(r, i))}
          </div>
        </div>
      )}

      {/* Groupe : maîtrisées (replié — synthèse cliquable) */}
      {groups.maitrise.length > 0 && (
        <div data-testid="ar-group-maitrise" style={{ marginBottom: 12 }}>
          <button className="ar-group-toggle" data-testid="ar-toggle-maitrise" onClick={() => setShowMaitrise((v) => !v)}
            style={groupToggleStyle}>
            {showMaitrise ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <CheckCircle2 size={15} color="var(--vert)" />
            <span style={{ flex: 1, textAlign: "left" }}>{groups.maitrise.length} interférence(s) maîtrisée(s)</span>
            <span style={{ fontSize: ".8rem", color: "var(--sarcelle)" }}>{showMaitrise ? "Masquer" : "Afficher"}</span>
          </button>
          {showMaitrise && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
              {groups.maitrise.map(({ r, i }) => renderRow(r, i))}
            </div>
          )}
        </div>
      )}

      {/* Groupe : écartées (replié) */}
      {groups.ecarte.length > 0 && (
        <div data-testid="ar-group-ecarte" style={{ marginBottom: 12 }}>
          <button className="ar-group-toggle" data-testid="ar-toggle-ecarte" onClick={() => setShowEcarte((v) => !v)}
            style={groupToggleStyle}>
            {showEcarte ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <AlertTriangle size={15} color="var(--text-muted)" />
            <span style={{ flex: 1, textAlign: "left" }}>{groups.ecarte.length} interférence(s) écartée(s)</span>
            <span style={{ fontSize: ".8rem", color: "var(--sarcelle)" }}>{showEcarte ? "Masquer" : "Afficher"}</span>
          </button>
          {showEcarte && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
              {groups.ecarte.map(({ r, i }) => renderRow(r, i))}
            </div>
          )}
        </div>
      )}

      {!rows.length && <div className="empty" style={{ textAlign: "left", padding: 0 }}>Aucune interférence détectée. Ajoutez un risque si nécessaire.</div>}

      {canEdit && (
        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          <button className="btn btn-g" data-testid="ar-add" onClick={addManuel}><Plus size={14} />Ajouter un risque</button>
          <button className="btn btn-p" data-testid="ar-save" onClick={save} disabled={busy}><Save size={14} />{busy ? "Enregistrement…" : "Enregistrer l'analyse"}</button>
        </div>
      )}
    </div>
  );

  // ---- Rendu d'une ligne R-xx : en-tête compacte (toujours visible) + détail repliable ----
  function renderRow(r, i) {
    const ecarte = r.statut === "ecarte";
    const blocSansMesure = !ecarte && r.conditionBloquante && !(r.mesure || "").trim();
    const isOpen = !!open[r.cle];
    const bColor = blocSansMesure ? "var(--rouge)" : ecarte ? "var(--text-muted)" : "var(--vert)";
    return (
      <div key={r.cle} id={`ar-row-${r.cle}`} data-testid={`ar-row-${r.cle}`} className="card"
        style={{ padding: 0, borderLeft: `3px solid ${bColor}`, opacity: ecarte ? 0.75 : 1, overflow: "hidden" }}>
        {/* En-tête compacte cliquable */}
        <button type="button" data-testid={`ar-head-${r.cle}`} onClick={() => toggle(r.cle)}
          style={{
            width: "100%", background: "transparent", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "10px 12px", textAlign: "left",
          }}>
          {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          <b>{r.ref || "R-—"}</b>
          <span className="tag" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {r.origine === "moteur" ? <><Cpu size={12} /> Moteur</> : <><Hand size={12} /> Manuel</>}
          </span>
          <span className={`badge b-${r.niveau === "stop" ? "elevee" : r.niveau === "warn" ? "moderee" : "faible"}`}>{r.niveauLabel}</span>
          {blocSansMesure ? <XCircle size={15} color="var(--rouge)" /> : ecarte ? <AlertTriangle size={15} color="var(--text-muted)" /> : <CheckCircle2 size={15} color="var(--vert)" />}
          {!isOpen && (
            <span style={{ flex: 1, minWidth: 0, fontSize: ".82rem", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.interference || r.risque || "—"}
            </span>
          )}
          {isOpen && <span style={{ flex: 1 }} />}
        </button>

        {/* Détail (profondeur à la demande) */}
        {isOpen && (
          <div className="ar-row-body" style={{ padding: "0 12px 12px" }}>
            {r.origine === "manuel" && canEdit && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
                <button className="iconbtn" data-testid={`ar-del-${r.cle}`} onClick={() => rmRow(i)}><Trash2 size={14} /></button>
              </div>
            )}
            {/* Phase → Zone → Créneau */}
            <div className="row3" style={{ marginBottom: 6 }}>
              <div><label className="f">Phase</label>
                <select data-testid={`ar-phase-${r.cle}`} value={r.phase || ""} disabled={!canEdit} onChange={(e) => upRow(i, "phase", e.target.value)}>
                  <option value="">—</option>
                  {phases.map((p) => <option key={p.code} value={p.code}>{p.code} · {p.intitule}</option>)}
                </select></div>
              <div><label className="f">Zone(s)</label>
                <input value={r.zonesLabel || ""} disabled={!canEdit || r.origine === "moteur"} onChange={(e) => upRow(i, "zonesLabel", e.target.value)} /></div>
              <div><label className="f">Créneau</label>
                <input value={r.creneau || ""} disabled={!canEdit || r.origine === "moteur"} onChange={(e) => upRow(i, "creneau", e.target.value)} /></div>
            </div>
            <div className="row2" style={{ marginBottom: 6 }}>
              <div><label className="f">Entreprise</label>
                <input value={r.entreprise || ""} disabled={!canEdit || r.origine === "moteur"} onChange={(e) => upRow(i, "entreprise", e.target.value)} /></div>
              <div><label className="f">Activité</label>
                <input value={r.activite || ""} disabled={!canEdit || r.origine === "moteur"} onChange={(e) => upRow(i, "activite", e.target.value)} /></div>
            </div>
            <div style={{ marginBottom: 6 }}>
              <label className="f">Interférence</label>
              <input value={r.interference || ""} disabled={!canEdit || r.origine === "moteur"} onChange={(e) => upRow(i, "interference", e.target.value)} />
            </div>
            {r.origine === "manuel" && (
              <div className="row2" style={{ marginBottom: 6 }}>
                <div><label className="f">Risque encouru</label>
                  <input value={r.risque || ""} disabled={!canEdit} onChange={(e) => upRow(i, "risque", e.target.value)} /></div>
                <div><label className="f">Niveau</label>
                  <select value={r.niveau} disabled={!canEdit} onChange={(e) => upRow(i, "niveau", e.target.value)}>
                    {NIVEAUX.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select></div>
              </div>
            )}
            {/* Mesure → Responsable → Condition bloquante */}
            <div className="row2" style={{ marginBottom: 6 }}>
              <div><label className="f">Mesure de prévention {blocSansMesure && <span style={{ color: "var(--rouge)" }}>— requise</span>}</label>
                <textarea rows={2} data-testid={`ar-mesure-${r.cle}`} value={r.mesure || ""} disabled={!canEdit || ecarte}
                  style={blocSansMesure ? { borderColor: "var(--rouge)" } : {}} onChange={(e) => upRow(i, "mesure", e.target.value)} /></div>
              <div><label className="f">Responsable</label>
                <input data-testid={`ar-resp-${r.cle}`} value={r.responsable || ""} disabled={!canEdit || ecarte} onChange={(e) => upRow(i, "responsable", e.target.value)} /></div>
            </div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ display: "flex", gap: 6, alignItems: "center", width: "auto", fontSize: ".82rem" }}>
                <input type="checkbox" style={{ width: "auto" }} data-testid={`ar-bloc-${r.cle}`} checked={!!r.conditionBloquante} disabled={!canEdit} onChange={(e) => upRow(i, "conditionBloquante", e.target.checked)} />
                Condition bloquante
              </label>
              <label style={{ display: "flex", gap: 6, alignItems: "center", width: "auto", fontSize: ".82rem" }}>
                <input type="checkbox" style={{ width: "auto" }} data-testid={`ar-ecarte-${r.cle}`} checked={ecarte} disabled={!canEdit} onChange={(e) => upRow(i, "statut", e.target.checked ? "ecarte" : "retenu")} />
                Écarter cette interférence
              </label>
            </div>
            {ecarte && (
              <div style={{ marginTop: 6 }}>
                <label className="f">Justification de l'écart</label>
                <input data-testid={`ar-justif-${r.cle}`} value={r.justificationEcart || ""} disabled={!canEdit} onChange={(e) => upRow(i, "justificationEcart", e.target.value)} />
              </div>
            )}
            <div style={{ fontSize: ".76rem", color: blocSansMesure ? "var(--rouge)" : "var(--text-muted)", marginTop: 6 }}>{r.explication}</div>
          </div>
        )}
      </div>
    );
  }
}

const groupToggleStyle = {
  width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
  borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)",
  cursor: "pointer", fontSize: ".85rem", fontWeight: 600,
};
